import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, RefreshCw, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getAccountOrders, getSubscriptions, cancelSubscription } from "@/lib/api";
import { money } from "@/lib/money";

export default function Account() {
  const { user, logout, openAuth } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) {
      openAuth("login");
      navigate("/");
    }
  }, [user, openAuth, navigate]);

  const load = async () => {
    try {
      const [o, s] = await Promise.all([getAccountOrders(), getSubscriptions()]);
      setOrders(o);
      setSubs(s);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to load account data:", e);
      toast.error("Couldn't load your account. Please refresh.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const doCancel = async (subId) => {
    try {
      await cancelSubscription(subId);
      toast.success("Subscription cancelled.");
      load();
    } catch (e) {
      toast.error("Could not cancel. Please try again.");
    }
  };

  if (!user) {
    return <div className="min-h-screen bg-[#080806] pt-[120px] text-center text-[#A1A1A6]">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[#080806] pt-[70px]">
      <div className="mx-auto max-w-4xl px-5 sm:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="font-mono-clinical text-xs uppercase tracking-[0.25em] text-[#D6A921]">My Account</p>
            <h1 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight text-white">Hi, {user.name.split(" ")[0]}</h1>
            <p className="mt-1 text-sm text-[#A1A1A6]">{user.email}</p>
          </div>
          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="account-logout"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-white hover:border-[#D6A921] hover:text-[#D6A921]">
            <LogOut size={15} /> Sign out
          </button>
        </div>

        <div className="mt-8 flex gap-2 border-b border-white/10">
          <button onClick={() => setTab("orders")} data-testid="account-tab-orders"
            className={`px-4 py-3 text-sm font-semibold transition-colors ${tab === "orders" ? "text-[#D6A921] border-b-2 border-[#D6A921]" : "text-[#A1A1A6]"}`}>Orders</button>
          <button onClick={() => setTab("subs")} data-testid="account-tab-subscriptions"
            className={`px-4 py-3 text-sm font-semibold transition-colors ${tab === "subs" ? "text-[#D6A921] border-b-2 border-[#D6A921]" : "text-[#A1A1A6]"}`}>Subscribe & Save</button>
        </div>

        {loading ? (
          <p className="mt-10 text-[#A1A1A6]">Loading…</p>
        ) : tab === "orders" ? (
          <div className="mt-8 space-y-4" data-testid="account-orders">
            {orders.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-[#161616] p-10 text-center">
                <Package size={30} className="mx-auto text-[#A1A1A6]" />
                <p className="mt-3 text-[#A1A1A6]">No orders yet.</p>
              </div>
            ) : orders.map((o) => (
              <div key={o.id} className="rounded-2xl border border-white/10 bg-[#161616] p-5">
                <div className="flex items-center justify-between">
                  <span className="font-mono-clinical text-sm text-[#D6A921]">{o.id.slice(0, 8).toUpperCase()}</span>
                  <span className="rounded-full bg-[#D6A921]/15 px-3 py-1 text-xs font-semibold text-[#D6A921] capitalize">{o.status}</span>
                </div>
                <div className="mt-3 space-y-1 text-sm text-[#F7F6F0]">
                  {o.items.map((it, i) => <p key={`${o.id}-item-${i}`}>{it.quantity}× {it.name}</p>)}
                </div>
                <div className="mt-3 flex justify-between border-t border-white/10 pt-3 text-sm">
                  <span className="text-[#A1A1A6] capitalize">{o.purchase_type}</span>
                  <span className="font-bold text-white">{money(o.total ?? o.subtotal)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-8 space-y-4" data-testid="account-subscriptions">
            {subs.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-[#161616] p-10 text-center">
                <RefreshCw size={30} className="mx-auto text-[#A1A1A6]" />
                <p className="mt-3 text-[#A1A1A6]">No active subscriptions.</p>
              </div>
            ) : subs.map((s) => {
              const active = s.subscription_status === "active" || s.subscription_status === "trialing";
              return (
                <div key={s.id} className="rounded-2xl border border-white/10 bg-[#161616] p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={16} className="text-[#D6A921]" />
                      <span className="font-semibold text-white">Monthly delivery</span>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${active ? "bg-[#D6A921]/15 text-[#D6A921]" : "bg-white/5 text-[#A1A1A6]"}`}>{s.subscription_status}</span>
                  </div>
                  <div className="mt-3 space-y-1 text-sm text-[#F7F6F0]">
                    {s.items.map((it, i) => <p key={`${s.id}-item-${i}`}>{it.quantity}× {it.name}</p>)}
                  </div>
                  {active && s.stripe_subscription_id && (
                    <button onClick={() => doCancel(s.stripe_subscription_id)} data-testid={`cancel-sub-${s.id}`}
                      className="mt-4 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-white hover:border-red-400 hover:text-red-400 transition-colors">
                      Cancel subscription
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

